import {
  toWSDateString,
  WSTableCell,
  WSTableColumn
} from "@wingspanhq/fe-component-library";
import { InvoiceRow, PayerRow } from "@wingspanhq/wingspan-api-sdk";
import { formatMoney } from "@wingspanhq/utils/dist/formatMoney";
import { selectorSearchRowName } from "../../../../shared/selectors/selectorSearchRowName";
import { selectorSearchRowSecondaryName } from "../../../../shared/selectors/selectorSearchRowSecondaryName";
import { getWorkLogPayeeStatusCellProps } from "../../utils/getWorkLogPayeeStatusCellProps";
import { TableItemData } from "./TableWorkLogsPayeeView";

type ColumnsConfig = {
  /** Client rows keyed by `payerEngagementId` — see `usePayersMap`. */
  payersMap: Map<string, PayerRow>;
  invoicesMap?: Map<string, InvoiceRow>;
};

export const getColumns = ({
  payersMap,
  invoicesMap
}: ColumnsConfig): WSTableColumn<TableItemData>[] => [
  {
    config: {
      header: {
        text: "Client"
      }
    },
    renderFunction: item => {
      // v3 WorkLog has no `payerId`; the client is joined off the counterpart
      // engagement id the work log does carry.
      const payer = item.data.payerEngagementId
        ? payersMap.get(item.data.payerEngagementId)
        : undefined;
      if (!payer) {
        return <WSTableCell text="-" />;
      }
      return (
        <WSTableCell
          text={selectorSearchRowName(payer)}
          secondaryText={selectorSearchRowSecondaryName(payer)}
          truncationOptions={{
            text: { allowTwoLines: true }
          }}
        />
      );
    }
  },
  {
    config: {
      header: {
        text: "Work log #"
      }
    },
    renderFunction: item => <WSTableCell text={item.data.workLogNumber} />
  },
  {
    config: {
      header: {
        text: "Amount"
      }
    },
    renderFunction: item => (
      <WSTableCell text={formatMoney(item.data.totalAmount ?? 0, "USD")} />
    )
  },
  {
    config: {
      header: {
        text: "Last modified"
      }
    },
    // TODO(v3): v3 WorkLog has no `updatedAt`; approximate "Last modified" with
    // the in-review event timestamp.
    renderFunction: item => (
      <WSTableCell
        text={toWSDateString(item.data.events?.inReviewAt, "timeframe")}
      />
    )
  },
  {
    config: {
      header: {
        text: "Status"
      }
    },
    renderFunction: item => {
      const invoice = invoicesMap?.get(item.data.convertedResourceId || "");

      // TODO(v3-blocker, WIN-16077): the engagement decides whether an unapproved work log
      // is described as becoming a pay statement or an invoice. SDK 4.2.0
      // dropped `engagementType` from the payer search row, and the engagement
      // template it now lives on belongs to the payer's account, so a contractor
      // cannot read it. Left unset, which falls back to the 1099 wording.
      const cellProps = getWorkLogPayeeStatusCellProps({
        workLog: item.data,
        invoice
      });

      return <WSTableCell {...cellProps} />;
    }
  }
];
